import { useAppSelector } from '../../../common/hooks'
import { CartItem } from '../../../store/data/types'
import { getTotalPrice } from '../../../common/cardUtils'
import { getPriceByMode } from '../../../common/productUtils'
import { Money } from '../../atoms'
import { getMissionsDiscount } from '../../../common/missions'
import {
  formatNumber,
  getShippingInfos,
  isInternalClubber as checkIfInternalClubber,
  getInstallmentsInfo,
} from '../../../common/utils'

import {
  CartCardContainer,
  CartDeliveryContainer,
  CartDeliveryWarningSpan,
  CartDetailDiv,
  CartDetailSpan,
  CartDetaiTwoDays,
  CartInstallmentsContainer,
  CartInstallmentsDiv,
  CartProfitabilities,
  CartProfitabilitiesSpan,
  CartSubtotalInfo,
  CartTitle,
  CartTotalSpan,
  MissionsDiscountPrice,
  StyledDeliveryFree,
  StyledHR,
} from './styles/CartPurchaseInfoCard.style'
import { ButtonProfitabilities } from '../../atoms/ButtonProfitabilities'
import TwoDaysTag from '../../atoms/TwoDaysTag'
import { TWO_DAYS_STORES } from '../../../common/constants'
import { CartPurchaseInfoCardProps } from './interfaces'

export const CartPurchaseInfoCard = ({ formValue }: CartPurchaseInfoCardProps) => {
  const missions = useAppSelector((state) => state.missions)
  const { shippingType } = useAppSelector((state) => state.shippingInfos)
  const totalProfitabilities = useAppSelector((state) => state.profitabilities.total)
  const { cart } = useAppSelector((state) => state)
  const session = useAppSelector((state) => state.session)
  const { clubberEmail, clubberArea } = useAppSelector((state) => state.session)

  const isInternalClubber = checkIfInternalClubber(clubberEmail, clubberArea)

  const fee = getShippingInfos(shippingType).value
  const discountAmount = getMissionsDiscount(missions, cart, fee)

  const getCartItemPrice = (cartItem: CartItem) => {
    const { getMoreForLess } = cartItem.product.supplierPrices[0]
    if (getMoreForLess) {
      return +getTotalPrice(cartItem.product, 0, cartItem.quantity)
    }
    return cartItem.quantity * getPriceByMode(cartItem.isPackageMode, cartItem.product)!
  }

  const cartItems = Object.values(cart).filter((item) => item.quantity > 0)

  const totalPrice = cartItems.reduce(
    (x: number, cartItem: CartItem) => x + getCartItemPrice(cartItem),
    0
  )

  const { twoDaysSubtotal, otherSubtotal, hasTwoDaysItems } = cartItems.reduce(
    (acc, cartItem) => {
      const { supplierId } = cartItem.product.supplierPrices[0]
      const itemPrice = getCartItemPrice(cartItem)
      if (TWO_DAYS_STORES.includes(supplierId)) {
        acc.twoDaysSubtotal += itemPrice
        acc.hasTwoDaysItems = true
      } else {
        acc.otherSubtotal += itemPrice
      }
      return acc
    },
    { twoDaysSubtotal: 0, otherSubtotal: 0, hasTwoDaysItems: false }
  )

  const totalValue = totalPrice + fee - discountAmount

  const isPaymentInstallmentEnabled = session.isPaymentInstallmentEnabled == true

  const installmentsInfos = isPaymentInstallmentEnabled
    ? getInstallmentsInfo(session.paymentDeadline, formValue.installments, totalValue)
    : []

  const showInstallments =
    formValue.paymentType !== -1 &&
    formValue.installments !== -1 &&
    isPaymentInstallmentEnabled &&
    installmentsInfos

  return (
    <CartCardContainer>
      <StyledHR />
      <CartTitle>Resumo do Pedido:</CartTitle>
      <CartDetailDiv>
        <CartDetailSpan>{'Subtotal'}</CartDetailSpan>
        <CartDetailSpan title="products-label" id="products-label">
          <Money amount={otherSubtotal} />
        </CartDetailSpan>
      </CartDetailDiv>
      {hasTwoDaysItems && <CartSubtotalInfo>Produtos entregues no dia seguinte</CartSubtotalInfo>}
      {hasTwoDaysItems && (
        <CartDetailDiv>
          <CartDetaiTwoDays>
            <CartDetailSpan>{hasTwoDaysItems ? 'Subtotal Parceiros' : 'Subtotal'}</CartDetailSpan>
            <TwoDaysTag openTooltip={false} />
          </CartDetaiTwoDays>
          <CartDetailSpan title="products-two-days" id="products-two-days">
            <Money amount={twoDaysSubtotal} />
          </CartDetailSpan>
        </CartDetailDiv>
      )}
      {hasTwoDaysItems && (
        <CartSubtotalInfo>Produtos entregues em prazo maiores de que 1 dia</CartSubtotalInfo>
      )}
      {!!discountAmount && (
        <CartDetailDiv>
          <CartDetailSpan>Desconto das missões</CartDetailSpan>
          <CartDetailSpan title="mission-discount-label" id="mission-discount-label">
            <MissionsDiscountPrice>
              -<Money amount={discountAmount} />
            </MissionsDiscountPrice>
          </CartDetailSpan>
        </CartDetailDiv>
      )}

      <CartDetailDiv>
        <CartDeliveryContainer>
          <CartDetailSpan>Frete</CartDetailSpan>
          <CartDeliveryWarningSpan>
            (Taxa única para compras da mesma entrega)
          </CartDeliveryWarningSpan>
        </CartDeliveryContainer>
        <CartDetailSpan title="delivery-label" id="delivery-label">
          {fee > 0 ? <Money amount={fee} /> : <StyledDeliveryFree>Grátis</StyledDeliveryFree>}
        </CartDetailSpan>
      </CartDetailDiv>
      <StyledHR />

      {showInstallments && (
        <>
          <CartInstallmentsDiv>
            <CartDetailSpan>Você pagará em:</CartDetailSpan>
            <CartInstallmentsContainer>
              {installmentsInfos.map((installment, index) => (
                <CartDetailDiv key={installment.installment}>
                  <CartDeliveryWarningSpan>
                    {session.isInstantPayment ? (
                      'À vista durante a entrega'
                    ) : installmentsInfos.length === 1 ? (
                      <>
                        Parcela única (<b>{installment.days} dias</b> após a entrega)
                      </>
                    ) : (
                      <>
                        Parcela {installment.installment}/{installmentsInfos.length} (
                        <b>{installment.days} dias</b> após a entrega)
                      </>
                    )}
                  </CartDeliveryWarningSpan>
                  <CartDeliveryWarningSpan>
                    <Money amount={installment.value} />
                  </CartDeliveryWarningSpan>
                </CartDetailDiv>
              ))}
            </CartInstallmentsContainer>
          </CartInstallmentsDiv>
          <StyledHR />
        </>
      )}

      <CartDetailDiv>
        <CartTotalSpan hasProfitabilities={!!isInternalClubber && !!totalProfitabilities}>
          Total:
        </CartTotalSpan>
        <CartTotalSpan
          title="total-label"
          hasProfitabilities={!!isInternalClubber && !!totalProfitabilities}
        >
          <Money amount={totalPrice + fee - discountAmount} />
        </CartTotalSpan>
      </CartDetailDiv>

      {isInternalClubber && totalProfitabilities && (
        <CartProfitabilities>
          <CartDetailDiv>
            <CartProfitabilitiesSpan>$/Kg</CartProfitabilitiesSpan>
            <CartProfitabilitiesSpan
              isNegative={totalProfitabilities.marginByKg < 0}
            >{`R$ ${formatNumber(totalProfitabilities.marginByKg)}/Kg`}</CartProfitabilitiesSpan>
          </CartDetailDiv>
          <CartDetailDiv>
            <CartProfitabilitiesSpan>LC1</CartProfitabilitiesSpan>
            <CartProfitabilitiesSpan isNegative={totalProfitabilities.lc1 < 0}>{`R$ ${formatNumber(
              totalProfitabilities.lc1
            )}`}</CartProfitabilitiesSpan>
          </CartDetailDiv>
          <CartDetailDiv>
            <CartProfitabilitiesSpan>LC1%</CartProfitabilitiesSpan>
            <CartProfitabilitiesSpan
              isNegative={totalProfitabilities.lc1Percent < 0}
            >{`${totalProfitabilities.lc1Percent}%`}</CartProfitabilitiesSpan>
          </CartDetailDiv>
        </CartProfitabilities>
      )}
      {isInternalClubber && <ButtonProfitabilities />}
    </CartCardContainer>
  )
}
